import { Component, Output, EventEmitter, OnInit } from '@angular/core';
import { SidebarStateService } from '../../../services/sidebar-state/sidebar-state.service';
import { IconsModule } from '../../../icons/icons.module';
import { CommonModule } from '@angular/common';
import { SidebarMenuComponent } from '../zikata-sidebar-menu/zikata-sidebar-menu.component';

@Component({
  selector: 'zikatagov-sidebar',
  template: `
    <!-- Mobile backdrop -->
    <div
      class="sidebar-backdrop"
      *ngIf="isMobile && mobileOpen"
      (click)="closeMobile()"
    ></div>

    <aside
      class="sidebar"
      [class.sidebar--mobile]="isMobile"
      [class.sidebar--mobile-open]="isMobile && mobileOpen"
      aria-label="Main navigation"
    >
      <!-- Top: Logo -->
      <div class="sidebar-top">
        <div class="sidebar-logo">
          <img
            src="https://zikata.africa/wp-content/uploads/2024/03/Artboard-5-copy-3-300x101.png"
            alt="Zikata"
            class="sidebar-logo-img"
          />
        </div>
      </div>

      <!-- Divider -->
      <div class="sidebar-divider"></div>

      <!-- Menu -->
      <div class="sidebar-menu-wrap">
        <zikatagov-sidebar-menu
          [showOnlyIcons]="false"
          [activeItemId]="activeItemId"
          (menuItemClick)="onMenuItemClick($event)"
        ></zikatagov-sidebar-menu>
      </div>

      <!-- Bottom: Logout -->
      <div class="sidebar-bottom">
        <div class="sidebar-divider"></div>
        <button class="logout-btn" (click)="onLogout()">
          <span class="logout-icon">
            <ng-icon name="saxLogoutOutline" size="17"></ng-icon>
          </span>
          <span class="logout-label">Logout</span>
        </button>
      </div>
    </aside>
  `,
  standalone: true,
  imports: [CommonModule, IconsModule, SidebarMenuComponent],
  styles: [`
    :host {
      font-family: 'DM Sans', 'Segoe UI', sans-serif;
    }

    /* ── Backdrop (mobile only) ── */
    .sidebar-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(60, 0, 100, 0.18);
      backdrop-filter: blur(3px);
      z-index: 1029;
      animation: fadeIn 0.2s ease;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }

    /* ── Sidebar shell ── */
    .sidebar {
      display: flex;
      flex-direction: column;
      width: 240px;
      height: 100%;
      background: #ffffff;
      border-right: 1.5px solid #f0eafa;
      box-shadow: 2px 0 16px rgba(109, 40, 217, 0.06);
      overflow: hidden;
      position: relative;
      z-index: 1030;
      flex-shrink: 0;
    }

    /* ── Mobile ── */
    .sidebar--mobile {
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      width: 260px;
      transform: translateX(-100%);
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 1030;
    }

    .sidebar--mobile-open {
      transform: translateX(0);
      box-shadow: 6px 0 32px rgba(109, 40, 217, 0.12);
    }

    /* ── Top section ── */
    .sidebar-top {
      display: flex;
      align-items: center;
      padding: 1.125rem 1rem;
      min-height: 64px;
      flex-shrink: 0;
    }

    .sidebar-logo {
      display: flex;
      align-items: center;
    }

    .sidebar-logo-img {
      width: 120px;
      height: auto;
      object-fit: contain;
      display: block;
    }

    /* ── Divider ── */
    .sidebar-divider {
      height: 1px;
      background: linear-gradient(90deg, transparent, #f0eafa 30%, #f9d5ea 70%, transparent);
      flex-shrink: 0;
      margin: 0 0.875rem;
    }

    /* ── Menu area ── */
    .sidebar-menu-wrap {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 0.625rem 0;
      scrollbar-width: none;
    }

    .sidebar-menu-wrap::-webkit-scrollbar {
      display: none;
    }

    /* ── Bottom / logout ── */
    .sidebar-bottom {
      flex-shrink: 0;
      padding-bottom: 0.75rem;
    }

    .logout-btn {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
      padding: 0.65rem 1rem;
      margin-top: 0.375rem;
      background: transparent;
      border: none;
      cursor: pointer;
      color: #9ca3af;
      transition: color 0.15s, background 0.15s;
      white-space: nowrap;
      overflow: hidden;
    }

    .logout-btn:hover {
      color: #db2777;
      background: linear-gradient(90deg, #fff0f7, transparent);
    }

    .logout-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      border-radius: 10px;
      background: #faf8ff;
      border: 1.5px solid #f0eafa;
      flex-shrink: 0;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
    }

    .logout-btn:hover .logout-icon {
      background: #fff0f7;
      border-color: #fbcfe8;
      color: #db2777;
    }

    .logout-label {
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: -0.01em;
    }
  `],
})
export class SidebarComponent implements OnInit {
  @Output() menuItemClick = new EventEmitter<number>();
  @Output() logout = new EventEmitter<void>();

  isMobile = false;
  mobileOpen = false;
  activeItemId: number | null = null;

  constructor(private sidebarStateService: SidebarStateService) {}

  ngOnInit() {
    this.sidebarStateService.activeItemId$.subscribe(
      (activeItemId) => (this.activeItemId = activeItemId)
    );
    this.sidebarStateService.isMobile$.subscribe(
      (isMobile) => (this.isMobile = isMobile)
    );
  }

  onMenuItemClick(id: number) {
    this.sidebarStateService.setActiveItem(id);
    this.menuItemClick.emit(id);
    if (this.isMobile) {
      this.mobileOpen = false;
    }
  }

  onLogout() {
    this.logout.emit();
  }

  openMobile() {
    this.mobileOpen = true;
  }

  closeMobile() {
    this.mobileOpen = false;
  }
}